import React, { useEffect, useCallback, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import classnames from 'classnames'
import { Select } from 'components/forms/Select'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { Link } from 'transfix-ui/core'
import { IParamsState, IShipmentFiltersState } from 'types/shipment'
import { getBidsCount } from 'utils/actions/bids'
import { NOTIFICATION_ACTION_TYPES, handleNotification } from 'utils/actions/notification'
import { getShipments } from 'utils/actions/shipments'
import { tabClicked, closeDialog } from 'utils/actions/ui'
import { shipmentTabsCountSelector } from 'utils/selectors/shipmentsCount'
import { ConnectedBookingFilters as BookingFilters } from 'apps/booking/components/Booking'
import CoverLoads from 'apps/booking/components/CoverLoads'
import { SHIPMENT_STATS_MAP, ON_HOLD_STATUS, StatsKeys } from 'apps/booking/constants/shipments'
import { filtersUpdate } from 'apps/booking/utils/actions/filters'
import { handleSidebar } from 'apps/booking/utils/actions/sidebar'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { Pagination } from 'components/Pagination'
import { TabContainer, Tab } from 'components/layout'
import { useModal } from 'utils/hooks'
import { parseQueryString } from 'utils/parseQueryString'
import { SHIPMENT_TABS } from '../../constants/shipments'
import { SORTING } from '../../constants/sorting'
import { useMultiSelectShipments } from '../../utils/hooks/useMultiSelectShipments'
import { searchParamsSelector } from '../../utils/selectors/filters'
import { notificationsSelector } from '../../utils/selectors/notification'
import { shipmentListSelector } from '../../utils/selectors/shipments'
import { sidebarStateSelector, sidebarShipmentSelector } from '../../utils/selectors/sidebar'
import { uiSelector } from '../../utils/selectors/ui'
import Sidebar from '../Sidebar'
import { BookingDialog } from './BookingDialog'
import { BookingInfoMessage } from './BookingInfoMessage'
import { BookingTable } from './BookingTable'
import IssueStateSidebar from './IssueStateSidebar'
import { SelectedShipmentsConfirmationModal } from './SelectedShipmentsConfirmationModal'
import { SelectedShipmentsFooter } from './SelectedShipmentsFooter'

export const Booking = React.memo(() => {
  const { trackEvent } = useMPAnalytics()
  const { hasNextPage, isFetching, params, shipmentList } = useSelector(shipmentListSelector)
  const { cta, notificationMessage, notificationTheme, show } = useSelector(notificationsSelector)
  const { dialogId, dialogOpen, selectedTabIndex, statusUpdate } = useSelector(uiSelector)
  const { shipmentsCount } = useSelector(shipmentTabsCountSelector)
  const { searchParams } = useSelector(searchParamsSelector)
  const { shipment } = useSelector(sidebarShipmentSelector)
  const { open: sidebarOpen } = useSelector(sidebarStateSelector)
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const [sidebarExpanded, setSidebarExpanded] = useState(false)
  const [currentShipmentId, setCurrentShipmentId] = useState<number>(0)
  const [selectedShipments, setSelectedShipments] = useState<number[]>([])
  const {
    acceptShipments,
    acceptSingleShipment,
    acceptedShipments,
    bulkRequestPending,
    rejectShipments,
    rejectSingleShipment,
    rejectedShipments,
  } = useMultiSelectShipments()
  const { closeModal, isOpen, openModal } = useModal()
  const [
    acceptMultipleConfirmationModal,
    acceptSingleConfirmationModal,
    rejectMultipleConfirmationModal,
    rejectSingleConfirmationModal,
  ] = [
    'acceptMultipleConfirmationModal',
    'acceptSingleConfirmationModal',
    'rejectMultipleConfirmationModal',
    'rejectSingleConfirmationModal',
  ]
  const openMultiAcceptConfirmationModal = openModal(acceptMultipleConfirmationModal)
  const openSingleAcceptConfirmationModal = openModal(acceptSingleConfirmationModal)
  const openMultiRejectConfirmationModal = openModal(rejectMultipleConfirmationModal)
  const openSingleRejectConfirmationModal = openModal(rejectSingleConfirmationModal)

  const location = useLocation()
  const dispatch = useDispatch()

  const onGetShipments = useCallback(
    (searchFilters?: IParamsState, tabs = SHIPMENT_TABS) => {
      const { filters: paramFilters } = searchFilters || {}
      const paramStatus = [paramFilters?.stateOptions, paramFilters?.status].flat()
      const onHoldStatusString = ON_HOLD_STATUS.join()

      const filters = paramFilters?.includes?.length
        ? searchFilters
        : {
            ...searchFilters,
            filters: { ...paramFilters, includes: ['backhaul_eligible_lanes', 'open_issues'] },
          }
      dispatch(getShipments(filters, tabs))

      if (paramStatus.includes(onHoldStatusString)) {
        const indexOfOnHold = SHIPMENT_TABS.findIndex(tab => tab.statsKey === StatsKeys.onHold)
        dispatch(tabClicked(indexOfOnHold, SHIPMENT_TABS))
      }
      dispatch(handleSidebar())
    },
    [dispatch]
  )

  useEffect(() => {
    if (!sidebarOpen) {
      setSidebarExpanded(false)
    }
  }, [sidebarOpen])

  useEffect(() => {
    if (!location.search) {
      const { status } = SHIPMENT_TABS[selectedTabIndex]
      dispatch(filtersUpdate({ status }))
    }
    if (location.search.includes('query')) {
      const { query } = parseQueryString(location.search)
      onGetShipments(query, SHIPMENT_TABS)
    }
  }, [dispatch, location, onGetShipments])

  useEffect(() => {
    dispatch(getBidsCount(shipmentList.map(s => s.id)))
  }, [dispatch])

  const onTabClick = (tabIndex: number, filters: IShipmentFiltersState) => {
    dispatch(tabClicked(tabIndex, SHIPMENT_TABS))
    const { status } = SHIPMENT_TABS[tabIndex]
    const { stateOptions } = params.filters
    const stateOrStatusOptions = stateOptions || status
    const newParams = {
      ...params,
      filters: { ...filters, status: stateOrStatusOptions },
      pageCount: 0,
    }
    onGetShipments(newParams, SHIPMENT_TABS)
  }

  const handleSort = (value: { name: string; sortField: string; sortOrder: string }) => {
    const newValue = JSON.parse(JSON.stringify(value))
    delete newValue.name
    trackEvent('Booking', 'SORTED', { 'Sorted By': newValue })
    onGetShipments({ ...params, ...newValue })
  }

  const dismissNotificationHandler = () => {
    const { NOTIFICATION_HIDE } = NOTIFICATION_ACTION_TYPES
    dispatch(handleNotification({ type: NOTIFICATION_HIDE }))
  }

  const { statsKey } = SHIPMENT_TABS[selectedTabIndex]
  const hasStats = !sidebarExpanded && statsKey ? !!(SHIPMENT_STATS_MAP as any)[statsKey] : false

  const tabContainerClassName = classnames(
    'col-xs-12',
    { 'col-lg-11': hasStats },
    { 'col-lg-12': !hasStats }
  )

  const tableClassName = classnames(
    'col-xs-12',
    { 'col-md-5 col-lg-6 col-xl-7': !sidebarExpanded && sidebarOpen },
    { 'col-md-3': sidebarExpanded }
  )

  const sidebarClassName = classnames(
    { invisible: !sidebarExpanded && !sidebarOpen },
    { 'col-xs-12 col-md-7 col-lg-6 col-xl-5': !sidebarExpanded && sidebarOpen },
    { 'col-xs-12 col-md-9': sidebarExpanded }
  )

  const onCloseDialog = () => {
    dispatch(closeDialog())
  }

  const postTabClick = (tab: number, tabParams = searchParams) => {
    onTabClick(tab, tabParams)
  }

  const refreshShipments = () => {
    onGetShipments({ ...params })
  }
  const toastSuccess = (message: string) => {
    openToast({
      toastType: 'default',
      toastMessage: (
        <span>
          {message} <Link onClick={refreshShipments}>Refresh</Link>
        </span>
      ),
    })
  }
  const toastAlert = (message: string) => {
    openToast({ toastType: 'alert', toastMessage: message })
  }

  const onRequestError = (error: string) => {
    toastAlert(error)
    closeModal()
  }
  const onBulkRequestSuccess = () => {
    toastSuccess('Shipments sucessfully updated.')
    setSelectedShipments([])
    closeModal()
  }
  const onSingleRequestSuccess = () => {
    toastSuccess('Shipment successfully updated.')
    // If request was successful, make sure the shipment ID
    // is no longer "selected":
    setSelectedShipments(selectedShipments.filter(id => id !== currentShipmentId))
    if (sidebarOpen) {
      dispatch(handleSidebar())
    }
    closeModal()
  }
  const acceptMultiple = async () => {
    await acceptShipments(selectedShipments, onBulkRequestSuccess, onRequestError)
  }
  const acceptSingle = async () => {
    await acceptSingleShipment(currentShipmentId, onSingleRequestSuccess, onRequestError)
  }
  const rejectMultiple = async () => {
    await rejectShipments(selectedShipments, onBulkRequestSuccess, onRequestError)
  }
  const rejectSingle = async () => {
    await rejectSingleShipment(currentShipmentId, onSingleRequestSuccess, onRequestError)
  }

  const shipmentCanBeSelected = (shipmentId: number, shipmentStatusKey: string) => {
    return (
      !acceptedShipments.includes(shipmentId) &&
      !rejectedShipments.includes(shipmentId) &&
      shipmentStatusKey === 'e_submitted'
    )
  }
  const selectAllShipments = () =>
    setSelectedShipments(
      shipmentList
        .filter(shipment => shipmentCanBeSelected(shipment.id, shipment.status.key))
        .map(shipment => shipment.id)
    )
  const deselectAllShipments = () => setSelectedShipments([])
  const onRowSelectionChange = (shipmentId: number, isSelected: boolean) => {
    if (isSelected) {
      setSelectedShipments([...selectedShipments, shipmentId])
    } else {
      setSelectedShipments(selectedShipments.filter(id => id !== shipmentId))
    }
  }

  const onAcceptShipmentClick = (shipmentId: number) => {
    setCurrentShipmentId(shipmentId)
    openSingleAcceptConfirmationModal()
  }
  const onRejectShipmentClick = (shipmentId: number) => {
    setCurrentShipmentId(shipmentId)
    openSingleRejectConfirmationModal()
  }

  return (
    <div className='pt8'>
      <div className='booking-container'>
        <div className='ph6'>
          <BookingFilters />
        </div>
        <div className='pa6 pt2'>
          {notificationMessage && (
            <BookingInfoMessage
              message={notificationMessage}
              onDismissNotification={dismissNotificationHandler}
              cta={cta}
              show={show}
              theme={notificationTheme}
            />
          )}{' '}
          <div className='row collapse'>
            <div className='col-xs-12 col-lg-10'>
              <TabContainer
                dataTest='booking'
                activeTabIndex={selectedTabIndex}
                postTabClick={postTabClick}>
                {SHIPMENT_TABS.map((tab, tabIndex: number) => (
                  <Tab
                    key={tab.label}
                    label={tab.label}
                    count={shipmentsCount[tabIndex]}
                    postTabClick={postTabClick}
                  />
                ))}
              </TabContainer>
            </div>
            <div className='col-xs-12 col-lg-2 last'>
              <Select
                dataTest='sort-order'
                className='input-borderless'
                onChangeHandler={handleSort}
                options={SORTING}
              />
            </div>
          </div>
          <hr className='cf ma0' />
          {hasStats && (
            <div className='col-xs-12 col-lg-1 mt2'>
              <div className='row no-gutter collapse'>
                <div className='col-xs-6 col-lg-12'>
                  <IssueStateSidebar />
                </div>
              </div>
            </div>
          )}
          <div className={tabContainerClassName}>
            {isFetching ? (
              <div className='spinner' />
            ) : (
              <div className='row'>
                <div className={tableClassName} data-test='booking-table'>
                  <BookingTable
                    shipmentCanBeSelected={shipmentCanBeSelected}
                    selectedShipments={selectedShipments}
                    acceptedShipments={acceptedShipments}
                    rejectedShipments={rejectedShipments}
                    onAcceptShipmentClick={onAcceptShipmentClick}
                    onRejectShipmentClick={onRejectShipmentClick}
                    onRowSelectionChange={onRowSelectionChange}
                  />
                  {!isFetching && (
                    <Pagination
                      onClickHandler={onGetShipments}
                      params={params}
                      hasNextPage={hasNextPage}
                    />
                  )}
                </div>
                <div className={sidebarClassName}>
                  {shipment && (
                    <Sidebar
                      expanded={sidebarExpanded}
                      setExpanded={setSidebarExpanded}
                      onAcceptShipmentClick={onAcceptShipmentClick}
                      onRejectShipmentClick={onRejectShipmentClick}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <BookingDialog
          // @ts-ignore
          dialogOpen={dialogOpen}
          dialogId={dialogId}
          dialogStatusUpdate={statusUpdate}
          closeDialog={onCloseDialog}
          shipment={shipment}
        />
        <CoverLoads />
        <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      </div>
      {!sidebarOpen && selectedShipments.length > 0 && (
        <SelectedShipmentsFooter
          selectedShipments={selectedShipments}
          acceptShipments={openMultiAcceptConfirmationModal}
          rejectShipments={openMultiRejectConfirmationModal}
          selectAllShipments={selectAllShipments}
          deselectAllShipments={deselectAllShipments}
          disabled={bulkRequestPending}
        />
      )}
      {isOpen(acceptMultipleConfirmationModal) && (
        <SelectedShipmentsConfirmationModal
          onClose={closeModal}
          onConfirm={acceptMultiple}
          selectedShipmentsCount={selectedShipments.length}
          disabled={bulkRequestPending}
          isAcceptRequest
        />
      )}
      {isOpen(acceptSingleConfirmationModal) && (
        <SelectedShipmentsConfirmationModal
          onClose={closeModal}
          onConfirm={acceptSingle}
          selectedShipmentsCount={selectedShipments.length}
          disabled={bulkRequestPending}
          isSingleShipment
          isAcceptRequest
        />
      )}
      {isOpen(rejectMultipleConfirmationModal) && (
        <SelectedShipmentsConfirmationModal
          onClose={closeModal}
          onConfirm={rejectMultiple}
          selectedShipmentsCount={selectedShipments.length}
          disabled={bulkRequestPending}
          isAcceptRequest={false}
        />
      )}
      {isOpen(rejectSingleConfirmationModal) && (
        <SelectedShipmentsConfirmationModal
          onClose={closeModal}
          onConfirm={rejectSingle}
          selectedShipmentsCount={selectedShipments.length}
          disabled={bulkRequestPending}
          isAcceptRequest={false}
          isSingleShipment
        />
      )}
    </div>
  )
})

export default Booking
